import { makeObservable, observable, action, runInAction } from "mobx";
import {
  InventoryListingDetails,
  InventoryOpsPoDetail,
  PrintInward,
  ProjectInventoryOpsProductDetails,
  ProjectInventoryOpsProductDetailsForPurchaseOrder,
  ProjectInventoryOpsSummary,
  VendorInvoiceDetails,
  pickInventoryOpsStageTimestamps,
} from "../../../../models/ProjectManagement/ProjectInventoryProduct";
import ProjectInventoryOpsAPI from "../../api/ProjectInventoryOpsAPI";
import {
  InventoryOpsRemovedQty,
  InventoryOpsReversalAction,
  InventoryOpsReversalReq,
  InventoryOpsReversalStage,
  PrintInwardCancelReq,
  PrintInwardQcReq,
  PrintInwardQtyReq,
  PrintInwardReceivedReq,
  PrintOutwardCancelReq,
  PrintOutwardReq,
  RawInwardCancelReq,
  RawInwardQcReq,
  RawInwardQtyReq,
  RawInwardReceivedReq,
  VendorInvoice,
  VendorInvoiceType,
} from "../../../../models/ProjectManagement/InventoryOpsRequests";
import { PurchaseOrder } from "../../../../models/ProjectManagement/PurchaseOrder";
import dayjs, { Dayjs } from "dayjs";

/** Draft fields cleared when a stage's qty is cancelled, undone or reverted. */
const STAGE_TEMP_FIELDS: Record<
  InventoryOpsReversalStage,
  Partial<InventoryOpsPoDetail>
> = {
  "raw-inward": {
    temp_raw_inward_quantity: null,
    temp_raw_inward_sku_variants: null,
    temp_raw_inward_rejection_quantity: null,
    temp_raw_inward_tested_quantity: null,
    temp_raw_inward_qc_sku_variants: null,
    temp_raw_inward_rejection_reason: null,
    temp_raw_inward_qc_images: [],
  },
  "print-outward": {
    temp_print_outward_printer_id: null,
    temp_print_outward_printer_name: null,
    temp_print_outward_quantity: null,
    temp_print_outward_sku_variants: null,
    temp_print_outward_timeline: null,
  },
  "print-inward": {
    temp_print_inward_quantity: null,
    temp_print_inward_sku_variants: null,
    temp_print_inward_rejection_quantity: null,
    temp_print_inward_tested_quantity: null,
    temp_print_inward_qc_sku_variants: null,
    temp_print_inward_rejection_reason: null,
    temp_print_inward_qc_images: [],
  },
};

export class ProjectInventoryOpsStore {
  projectInventoryOpsAPI: ProjectInventoryOpsAPI;
  originalData: InventoryListingDetails;
  inventoryRequestBody: InventoryListingDetails;
  productDetails: ProjectInventoryOpsProductDetails;
  productDetailsBody: ProjectInventoryOpsProductDetails;
  /** PO-scoped SKU mixes, progress, and inventory ops row(s). */
  poDetailsBody: ProjectInventoryOpsProductDetailsForPurchaseOrder;
  summaryDetails: ProjectInventoryOpsSummary;
  currentPoDetails: InventoryOpsPoDetail;
  /** True when PO details loaded with a warehouse already set (e.g. from PR). */
  isWarehouseLockedFromPo: boolean = false;
  currentProductId: number | null = null;
  dialogType: "rawInward" | "printInward" | null = null;
  vendorInvoiceDetails: VendorInvoiceDetails;
  isRefreshing: boolean = false;

  constructor() {
    this.projectInventoryOpsAPI = new ProjectInventoryOpsAPI();
    this.originalData = {} as InventoryListingDetails;
    this.inventoryRequestBody = {} as InventoryListingDetails;
    this.productDetails = {} as ProjectInventoryOpsProductDetails;
    this.productDetailsBody = {} as ProjectInventoryOpsProductDetails;
    this.poDetailsBody = {
      size_mixes: [],
      progress: 0,
      inventory_ops_details: [],
    };
    this.summaryDetails = {} as ProjectInventoryOpsSummary;
    this.currentPoDetails = {} as InventoryOpsPoDetail;
    this.isWarehouseLockedFromPo = false;
    this.vendorInvoiceDetails = {};

    makeObservable(this, {
      originalData: observable,
      inventoryRequestBody: observable,
      productDetails: observable,
      productDetailsBody: observable,
      poDetailsBody: observable,
      summaryDetails: observable,
      currentPoDetails: observable,
      isWarehouseLockedFromPo: observable,
      dialogType: observable,
      isRefreshing: observable,
      refreshData: action,
      getProjectInventoryOpsDetails: action,
      getProjectInventoryOpsProductDetails: action,
      getProjectInventoryOpsProductDetailsForPurchaseOrder: action,
      clearPoScopedDetails: action,
      getSummary: action,
      setCurrentPoDetails: action,
      updateCurrentPoDetails: action,
      markRawInwardReceived: action,
      markPrintInwardReceived: action,
      updateRawInwardQty: action,
      updateRawInwardQc: action,
      updatePrintOutward: action,
      updatePrintInwardQty: action,
      updatePrintInwardQc: action,
      cancelRawInward: action,
      cancelPrintOutward: action,
      cancelPrintInward: action,
      reverseInventoryOpsStage: action,
      clearPopupData: action,
      updateRawInwardDate: action,
      updatePrintInwardDate: action,
      setDialogType: action,
      setCurrentProductId: action,
      deleteRawInwardQCImg: action,
      deletePrintInwardQCImg: action,
      vendorInvoiceDetails: observable,
      fetchVendorInvoices: action,
      addUpdateVendorInvoices: action,
      updateInvoiceMarkedAsReceived: action,
    });
  }

  getProjectInventoryOpsDetails = async (projectId: number) => {
    try {
      const response =
        await this.projectInventoryOpsAPI.getInventoryOpsProducts(projectId);
      this.originalData = response;
      this.inventoryRequestBody = response;
    } catch (error: any) {
      throw error;
    }
  };

  getProjectInventoryOpsProductDetails = async (
    projectId: number,
    productId: number,
  ) => {
    try {
      const response =
        await this.projectInventoryOpsAPI.getProjectInventoryOpsProductDetails(
          projectId,
          productId,
        );
      runInAction(() => {
        this.productDetails = response;
        this.productDetailsBody = response;
      });
    } catch (error: any) {
      throw error;
    }
  };

  getProjectInventoryOpsProductDetailsForPurchaseOrder = async (
    projectId: number,
    productId: number,
    poId: number,
  ) => {
    try {
      const response =
        await this.projectInventoryOpsAPI.getProjectInventoryOpsProductDetailsForPurchaseOrder(
          projectId,
          productId,
          poId,
        );
      runInAction(() => {
        this.poDetailsBody = {
          ...pickInventoryOpsStageTimestamps(response),
          size_mixes: response.size_mixes || [],
          progress: response.progress ?? 0,
          inventory_ops_details: response.inventory_ops_details || [],
        };
        this.setCurrentPoDetails(poId);
      });
    } catch (error: any) {
      throw error;
    }
  };

  /**
   * Re-fetches the product screen in place (product list, product, summary and
   * the currently selected PO) without a full page reload. Falls back to the
   * first PO when the selected one no longer exists.
   * Returns the PO id that ended up selected (null when the product has none).
   */
  refreshData = async (
    projectId: number,
    productId: number,
  ): Promise<number | null> => {
    const selectedPoId = this.currentPoDetails?.po_id;
    runInAction(() => {
      this.isRefreshing = true;
    });
    try {
      await Promise.all([
        this.getProjectInventoryOpsDetails(projectId),
        this.getProjectInventoryOpsProductDetails(projectId, productId),
        this.getSummary(projectId, productId),
      ]);

      const purchaseOrders = this.productDetailsBody.purchase_orders || [];
      const poId =
        purchaseOrders.find((po) => po.id === selectedPoId)?.id ??
        purchaseOrders[0]?.id;

      if (!poId) {
        this.clearPoScopedDetails();
        return null;
      }

      await this.getProjectInventoryOpsProductDetailsForPurchaseOrder(
        projectId,
        productId,
        poId,
      );
      return poId;
    } finally {
      runInAction(() => {
        this.isRefreshing = false;
      });
    }
  };

  clearPoScopedDetails = () => {
    runInAction(() => {
      this.poDetailsBody = {
        size_mixes: [],
        progress: 0,
        inventory_ops_details: [],
      };
      this.currentPoDetails = {} as InventoryOpsPoDetail;
      this.isWarehouseLockedFromPo = false;
    });
  };

  setCurrentPoDetails = (poId: number) => {
    const inventoryDetail =
      this.poDetailsBody.inventory_ops_details?.find(
        (podetails) => podetails.po_id === poId,
      ) ?? this.poDetailsBody.inventory_ops_details?.[0];

    const purchaseOrder = this.productDetailsBody.purchase_orders?.find(
      (po) => po.id === poId,
    );

    const poWarehouseId = purchaseOrder?.warehouse_id
      ? String(purchaseOrder.warehouse_id)
      : null;

    this.currentPoDetails = {
      ...(inventoryDetail || {}),
      ...pickInventoryOpsStageTimestamps(inventoryDetail, this.poDetailsBody),
      po_id: poId,
      product_po_quantity: purchaseOrder ? purchaseOrder.quantity : 0,
      vendor_name: purchaseOrder ? purchaseOrder.vendor_name : "",
      ...(poWarehouseId
        ? {
            warehouse_id: poWarehouseId,
            warehouse_identifier:
              purchaseOrder?.warehouse_identifier ||
              inventoryDetail?.warehouse_identifier ||
              poWarehouseId,
          }
        : {}),
    };
    this.isWarehouseLockedFromPo = Boolean(poWarehouseId);
  };

  checkProductIndex = (productId: number): number => {
    if (
      !this.inventoryRequestBody.products ||
      this.inventoryRequestBody.products.length === 0
    )
      return -1;

    const productList = this.inventoryRequestBody.products;
    const currentIndex = productList.findIndex(
      (product) => product.id === productId,
    );

    return currentIndex;
  };

  /**
   * Merges the mutated `inventory_ops_details` row back into the PO detail body,
   * refreshes `currentPoDetails` and clears the given temp fields.
   *
   * Important: mutation payloads are often partial. Replace-merge would drop
   * existing fields (e.g. raw_inward_sku_variants / raw_inward_id) and zero out
   * downstream remaining qty / disable Print Outward.
   */
  private applyMutationResponse = (
    response: Partial<ProjectInventoryOpsProductDetailsForPurchaseOrder>,
    tempFieldsToClear: Partial<InventoryOpsPoDetail>,
  ) => {
    const updatedDetail: InventoryOpsPoDetail | undefined =
      response?.inventory_ops_details?.[0];
    if (!updatedDetail) return;

    runInAction(() => {
      const details = this.poDetailsBody.inventory_ops_details || [];
      const index = details.findIndex(
        (detail) => detail.po_id === updatedDetail.po_id,
      );
      const responseTimestamps = pickInventoryOpsStageTimestamps(
        updatedDetail,
        response,
      );

      if (index !== -1) {
        const previous = details[index];
        details[index] = {
          ...previous,
          ...updatedDetail,
          ...pickInventoryOpsStageTimestamps(responseTimestamps, previous),
          // Keep prior SKU arrays when the mutation response omits them.
          raw_inward_sku_variants:
            updatedDetail.raw_inward_sku_variants ??
            previous.raw_inward_sku_variants,
          raw_inward_qc_sku_variants:
            updatedDetail.raw_inward_qc_sku_variants ??
            previous.raw_inward_qc_sku_variants,
          print_outward_sku_variants:
            updatedDetail.print_outward_sku_variants ??
            previous.print_outward_sku_variants,
          print_inward_sku_variants:
            updatedDetail.print_inward_sku_variants ??
            previous.print_inward_sku_variants,
          print_inward_qc_sku_variants:
            updatedDetail.print_inward_qc_sku_variants ??
            previous.print_inward_qc_sku_variants,
        };
      } else {
        details.push({
          ...updatedDetail,
          ...responseTimestamps,
        });
      }

      this.poDetailsBody = {
        ...this.poDetailsBody,
        ...responseTimestamps,
      };

      this.poDetailsBody.inventory_ops_details = details;
      this.setCurrentPoDetails(updatedDetail.po_id);
      this.updateCurrentPoDetails(tempFieldsToClear);
      this.poDetailsBody.progress = response.progress ?? 0;
    });
  };

  markRawInwardReceived = async (reqBody: RawInwardReceivedReq) => {
    const response =
      await this.projectInventoryOpsAPI.markRawInwardReceived(reqBody);
    this.applyMutationResponse(response, {});
  };

  markPrintInwardReceived = async (reqBody: PrintInwardReceivedReq) => {
    const response =
      await this.projectInventoryOpsAPI.markPrintInwardReceived(reqBody);
    this.applyMutationResponse(response, {});
  };

  updateRawInwardQty = async (reqBody: RawInwardQtyReq) => {
    try {
      const response = await this.projectInventoryOpsAPI.updateRawInwardQty(
        reqBody
      );
      this.applyMutationResponse(response, {
        temp_raw_inward_quantity: null,
        temp_raw_inward_sku_variants: null,
      });
    } catch (error: any) {
      throw error;
    }
  };

  updateRawInwardQc = async (reqBody: RawInwardQcReq) => {
    try {
      const response = await this.projectInventoryOpsAPI.updateRawInwardQc(
        reqBody
      );
      this.applyMutationResponse(response, {
        temp_raw_inward_rejection_quantity: null,
        temp_raw_inward_tested_quantity: null,
        temp_raw_inward_qc_sku_variants: null,
        temp_raw_inward_rejection_reason: null,
        temp_raw_inward_qc_images: [],
      });
    } catch (error: any) {
      throw error;
    }
  };

  updatePrintOutward = async (body: PrintOutwardReq) => {
    try {
      const response = await this.projectInventoryOpsAPI.updatePrintOutward(
        body
      );
      this.applyMutationResponse(response, {
        temp_print_outward_printer_id: null,
        temp_print_outward_printer_name: null,
        temp_print_outward_quantity: null,
        temp_print_outward_sku_variants: null,
        temp_print_outward_timeline: null,
      });
    } catch (error: any) {
      throw error;
    }
  };

  updatePrintInwardQty = async (body: PrintInwardQtyReq) => {
    try {
      const response = await this.projectInventoryOpsAPI.updatePrintInwardQty(
        body
      );
      this.applyMutationResponse(response, {
        temp_print_inward_quantity: null,
        temp_print_inward_sku_variants: null,
      });
    } catch (error: any) {
      throw error;
    }
  };

  updatePrintInwardQc = async (body: PrintInwardQcReq) => {
    try {
      const response = await this.projectInventoryOpsAPI.updatePrintInwardQc(
        body
      );
      this.applyMutationResponse(response, {
        temp_print_inward_rejection_quantity: null,
        temp_print_inward_tested_quantity: null,
        temp_print_inward_qc_sku_variants: null,
        temp_print_inward_rejection_reason: null,
        temp_print_inward_qc_images: [],
      });
    } catch (error: any) {
      throw error;
    }
  };

  cancelRawInward = async (body: RawInwardCancelReq) => {
    try {
      const response = await this.projectInventoryOpsAPI.cancelRawInward(body);
      this.applyMutationResponse(response, STAGE_TEMP_FIELDS["raw-inward"]);
    } catch (error: any) {
      throw error;
    }
  };

  cancelPrintOutward = async (body: PrintOutwardCancelReq) => {
    try {
      const response =
        await this.projectInventoryOpsAPI.cancelPrintOutward(body);
      this.applyMutationResponse(response, STAGE_TEMP_FIELDS["print-outward"]);
    } catch (error: any) {
      throw error;
    }
  };

  cancelPrintInward = async (body: PrintInwardCancelReq) => {
    try {
      const response =
        await this.projectInventoryOpsAPI.cancelPrintInward(body);
      this.applyMutationResponse(response, STAGE_TEMP_FIELDS["print-inward"]);
    } catch (error: any) {
      throw error;
    }
  };

  /**
   * Undo the last batch or revert all qty on one stage without cancelling it.
   * Applies the refreshed row and returns what was removed. Throws with the
   * server message as-is (it names what to undo first).
   */
  reverseInventoryOpsStage = async (
    stage: InventoryOpsReversalStage,
    action: InventoryOpsReversalAction,
    body: InventoryOpsReversalReq,
  ): Promise<InventoryOpsRemovedQty | null> => {
    try {
      const response = await this.projectInventoryOpsAPI.reverseInventoryOpsStage(
        stage,
        action,
        body,
      );
      this.applyMutationResponse(response, STAGE_TEMP_FIELDS[stage]);
      return response?.removed ?? null;
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.message ||
          error?.message ||
          `Failed to ${action === "undo-last" ? "undo the last batch" : "revert"}, please try again later.`,
      );
    }
  };

  updateCurrentPoDetails = (fields: Partial<typeof this.currentPoDetails>) => {
    runInAction(() => {
      this.currentPoDetails = { ...this.currentPoDetails, ...fields };
    });
  };

  setCurrentProductId = (productId: number | null) => {
    this.currentProductId = productId;
  };

  clearPopupData = () => {
    this.dialogType = null;
  };

  setDialogType = (type: "rawInward" | "printInward" | null) => {
    this.dialogType = type;
  };

  updateRawInwardDate = (newValue: Dayjs | null, index: number) => {
    if (!this.currentProductId) return;

    const productIndex = this.inventoryRequestBody.products?.findIndex(
      (product) => product.id === this.currentProductId
    );

    if (
      productIndex !== -1 &&
      this.inventoryRequestBody.products?.[productIndex]?.purchase_orders
    ) {
      runInAction(() => {
        this.inventoryRequestBody.products![productIndex].purchase_orders[
          index
        ].expected_delivery_date = newValue
          ? dayjs(newValue).format("YYYY-MM-DD 00:00:00")
          : undefined;
      });
    }
  };

  updatePrintInwardDate = (newValue: Dayjs | null, index: number) => {
    if (!this.currentProductId) return;

    const productIndex = this.inventoryRequestBody.products?.findIndex(
      (product) => product.id === this.currentProductId
    );

    if (
      productIndex !== -1 &&
      this.inventoryRequestBody.products?.[productIndex]?.print_inward_timelines
    ) {
      runInAction(() => {
        this.inventoryRequestBody.products![
          productIndex
        ].print_inward_timelines[index].timeline = newValue
          ? dayjs(newValue).format("YYYY-MM-DD 00:00:00")
          : undefined;
      });
    }
  };

  updateRawInwardExpectedDate = async (
    order_id: number,
    productId: number,
    poData: PurchaseOrder[]
  ) => {
    try {
      const payload = {
        order_id: order_id,
        product_id: productId,
        purchase_requests: poData
          .filter(
            (po) =>
              po.pr_id !== undefined && po.expected_delivery_date !== undefined
          )
          .map((po) => ({
            pr_id: po.pr_id as number,
            expected_delivery_date: po.expected_delivery_date as string,
          })),
      };

      await this.projectInventoryOpsAPI.updateRawInwardExpectedDate(payload);
    } catch (error: any) {
      throw error;
    }
  };

  updatePrintInwardTimeline = async (
    order_id: number,
    productId: number,
    poData: PrintInward[]
  ) => {
    try {
      const payload = {
        order_id: order_id,
        product_id: productId,
        raw_inwards: poData
          .filter(
            (po) => po.raw_inward_id !== undefined && po.po_id !== undefined
          )
          .map((po) => ({
            raw_inward_id: po.raw_inward_id as number,
            timeline: po.timeline as string,
            po_id: po.po_id as number,
          })),
      };

      await this.projectInventoryOpsAPI.updatePrintInwardTimeline(payload);
    } catch (error: any) {
      throw error;
    }
  };

  getSummary = async (projectId: number, productId: number) => {
    try {
      const response = await this.projectInventoryOpsAPI.getSummary(
        projectId,
        productId
      );
      this.summaryDetails = response;
    } catch (error: any) {
      throw error;
    }
  };

  deleteRawInwardQCImg = async (id: number) => {
    try {
      if (id) {
        const response = await this.projectInventoryOpsAPI.deleteRawInwardQCImg(
          id
        );
        if (response.success) {
          if (
            this.currentPoDetails &&
            this.currentPoDetails.raw_inward_qc_images
          ) {
            this.currentPoDetails.raw_inward_qc_images =
              this.currentPoDetails.raw_inward_qc_images.filter(
                (image) => image.id !== id
              );
          }
        }
      }
    } catch (error: any) {
      throw error;
    }
  };

  deletePrintInwardQCImg = async (id: number) => {
    try {
      if (id) {
        const response =
          await this.projectInventoryOpsAPI.deletePrintInwardQCImg(id);
        if (response) {
          if (
            this.currentPoDetails &&
            this.currentPoDetails?.print_inward_qc_images
          ) {
            this.currentPoDetails.print_inward_qc_images =
              this.currentPoDetails.print_inward_qc_images.filter(
                (image) => image.id !== id
              );
          }
        }
      }
    } catch (error: any) {
      throw error;
    }
  };

  fetchVendorInvoices = async (inwardId: number, type: VendorInvoiceType) => {
    try {
      const response = await this.projectInventoryOpsAPI.fetchVendorInvoices(
        inwardId,
        type
      );
      this.vendorInvoiceDetails = response;
    } catch (error: any) {
      throw error;
    }
  };

  addUpdateVendorInvoices = async (
    inwardId: number,
    type: VendorInvoiceType,
    body: VendorInvoice[]
  ) => {
    try {
      const response =
        await this.projectInventoryOpsAPI.addUpdateVendorInvoices(
          inwardId,
          type,
          body
        );
      this.vendorInvoiceDetails = response;
    } catch (error: any) {
      throw error;
    }
  };

  updateInvoiceMarkedAsReceived = async (
    inwardId: number,
    type: VendorInvoiceType,
    markedAsReceived: boolean
  ) => {
    try {
      await this.projectInventoryOpsAPI.updateInvoiceMarkedAsReceived(
        inwardId,
        type,
        markedAsReceived
      );
    } catch (error: any) {
      throw error;
    }
  };
}
